import React, { useState } from 'react';
import { Heart, Wind, Sun, Moon, Music, BookOpen, Phone, AlertCircle, CheckCircle, ChevronRight, Play, Pause, RotateCcw } from '../components/CustomIcons';

const WELLNESS_TOOLS = [
  {
    id: 'breathing', title: '4-7-8 Breathing Exercise', desc: 'A proven technique to reduce anxiety in minutes. Breathe in for 4 counts, hold for 7, exhale for 8.',
    icon: Wind, tag: 'Stress Relief', duration: '5 min'
  },
  {
    id: 'mindfulness', title: 'Body Scan Meditation', desc: 'Progressive relaxation from head to toe. Ideal for pre-sleep or after a stressful day.',
    icon: Sun, tag: 'Mindfulness', duration: '10 min'
  },
  {
    id: 'sleep', title: 'Sleep Hygiene Guide', desc: 'Research-backed strategies for better sleep quality in a shared living environment.',
    icon: Moon, tag: 'Sleep', duration: '3 min read'
  },
  {
    id: 'journaling', title: 'Guided Journaling Prompts', desc: 'Daily prompts to process emotions, build gratitude, and maintain mental clarity.',
    icon: BookOpen, tag: 'Journaling', duration: '10 min'
  },
];

const CRISIS_RESOURCES = [
  { name: 'Crisis Text Line', detail: 'Text HOME to 741741', desc: 'Free, 24/7 crisis support via text. Connects you with a trained counselor.', urgent: true },
  { name: 'National Suicide Prevention Lifeline', detail: 'Call or text 988', desc: 'Available 24/7 for emotional support and mental health crisis intervention.', urgent: true },
  { name: 'SAMHSA Helpline', detail: '1-800-662-4357', desc: 'Substance use and mental health treatment referrals. Free, confidential, 24/7.', urgent: false },
  { name: 'Veterans Crisis Line', detail: 'Dial 988, Press 1', desc: 'Specialized crisis support for veterans, service members, and their families.', urgent: false },
];

const MOOD_OPTIONS = [
  { emoji: '😊', label: 'Good', value: 5 },
  { emoji: '😌', label: 'Okay', value: 4 },
  { emoji: '😐', label: 'Neutral', value: 3 },
  { emoji: '😟', label: 'Low', value: 2 },
  { emoji: '😢', label: 'Struggling', value: 1 },
];

const SELF_CARE_CHECKLIST = [
  { id: 'sleep', label: 'Got 7+ hours of sleep' },
  { id: 'water', label: 'Drank enough water today' },
  { id: 'move', label: 'Physical movement or exercise' },
  { id: 'food', label: 'Ate at least 2 meals' },
  { id: 'connection', label: 'Connected with someone I trust' },
  { id: 'learning', label: 'Completed a lesson or reading' },
];

// Breathing Exercise Component
function BreathingExercise({ onClose }) {
  const [phase, setPhase] = useState('idle'); // idle, inhale, hold, exhale
  const [count, setCount] = useState(0);
  const [cycles, setCycles] = useState(0);

  const PHASES = [
    { name: 'inhale', label: 'Breathe In', duration: 4, color: '#F97316' },
    { name: 'hold', label: 'Hold', duration: 7, color: '#F5A623' },
    { name: 'exhale', label: 'Breathe Out', duration: 8, color: '#3B82F6' },
  ];

  const currentPhase = PHASES.find(p => p.name === phase) || PHASES[0];

  const startBreathing = () => {
    setPhase('inhale');
    setCount(4);
    const sequence = async () => {
      for (let i = 4; i >= 0; i--) {
        setCount(i);
        await new Promise(r => setTimeout(r, 1000));
      }
      setPhase('hold');
      for (let i = 7; i >= 0; i--) {
        setCount(i);
        await new Promise(r => setTimeout(r, 1000));
      }
      setPhase('exhale');
      for (let i = 8; i >= 0; i--) {
        setCount(i);
        await new Promise(r => setTimeout(r, 1000));
      }
      setCycles(c => c + 1);
      setPhase('idle');
    };
    sequence();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '48px', textAlign: 'center', width: '360px' }}>
        <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '20px', color: 'var(--text-primary)', margin: '0 0 8px' }}>4-7-8 Breathing</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 32px' }}>Cycles completed: {cycles}</p>

        <div style={{ width: '120px', height: '120px', borderRadius: '50%', border: `4px solid ${phase === 'idle' ? '#333333' : currentPhase.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', margin: '0 auto 24px', transition: 'border-color 0.5s', boxShadow: phase !== 'idle' ? `0 0 30px ${currentPhase.color}44` : 'none' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '40px', color: phase === 'idle' ? '#333333' : currentPhase.color }}>{phase === 'idle' ? '•' : count}</div>
          {phase !== 'idle' && <div style={{ fontSize: '12px', color: '#888888' }}>{currentPhase.label}</div>}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          {phase === 'idle' && (
            <button onClick={startBreathing} style={{ background: '#F97316', border: 'none', borderRadius: '10px', padding: '12px 28px', cursor: 'pointer', color: '#111111', fontWeight: 700, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Play size={14} /> Start
            </button>
          )}
          <button onClick={onClose} style={{ background: 'var(--bg-page)', border: '1px solid var(--border-default)', borderRadius: '10px', padding: '12px 28px', cursor: 'pointer', color: '#888888', fontSize: '14px' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Health() {
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodLogged, setMoodLogged] = useState(false);
  const [selfCare, setSelfCare] = useState({});
  const [showBreathing, setShowBreathing] = useState(false);

  const toggleSelfCare = (id) => setSelfCare(p => ({ ...p, [id]: !p[id] }));
  const selfCareDone = Object.values(selfCare).filter(Boolean).length;

  const handleLogMood = () => {
    if (selectedMood !== null) setMoodLogged(true);
  };

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="health-page">
      {showBreathing && <BreathingExercise onClose={() => setShowBreathing(false)} />}

      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Health & Wellness</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>Mental health tools, self-care tracking, and crisis resources.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Mood Check-In */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px' }} data-testid="mood-checkin">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Heart size={16} color="#F97316" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>Daily Mood Check-In</span>
              </div>
              {!moodLogged ? (
                <>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px' }}>How are you feeling today?</p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginBottom: '16px' }}>
                    {MOOD_OPTIONS.map(m => (
                      <button key={m.value} onClick={() => setSelectedMood(m.value)}
                        style={{ flex: 1, background: selectedMood === m.value ? 'rgba(249,115,22,0.15)' : 'transparent', border: `1px solid ${selectedMood === m.value ? 'var(--orange)' : 'var(--border)'}`, borderRadius: '10px', padding: '10px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
                        data-testid={`mood-${m.label.toLowerCase()}`}>
                        <span style={{ fontSize: '22px' }}>{m.emoji}</span>
                        <span style={{ fontSize: '10px', color: selectedMood === m.value ? '#F97316' : '#555555' }}>{m.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={handleLogMood} disabled={selectedMood === null}
                    style={{ width: '100%', background: selectedMood !== null ? 'var(--orange)' : 'var(--surface-alt)', border: 'none', borderRadius: '10px', padding: '11px', cursor: selectedMood !== null ? 'pointer' : 'default', color: selectedMood !== null ? '#ffffff' : 'var(--text-muted)', fontWeight: 600, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s' }}>
                    Log Mood
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <CheckCircle size={36} color="#22C55E" style={{ marginBottom: '12px' }} />
                  <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: '#22C55E' }}>Mood logged!</div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0 0' }}>Keep checking in daily to track your mental health trends.</p>
                </div>
              )}
            </div>

            {/* Self-Care Checklist */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px' }} data-testid="self-care-checklist">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sun size={16} color="#F97316" />
                  <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>Today's Self-Care</span>
                </div>
                <span style={{ fontSize: '13px', color: '#F97316', fontWeight: 600 }}>{selfCareDone}/{SELF_CARE_CHECKLIST.length}</span>
              </div>
              {SELF_CARE_CHECKLIST.map(item => (
                <div key={item.id} onClick={() => toggleSelfCare(item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)' }}
                  data-testid={`selfcare-${item.id}`}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: selfCare[item.id] ? 'none' : '2px solid var(--border)', background: selfCare[item.id] ? '#22C55E' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selfCare[item.id] && <span style={{ color: '#111111', fontSize: '11px' }}>✓</span>}
                  </div>
                  <span style={{ fontSize: '14px', color: selfCare[item.id] ? '#555555' : '#CCCCCC', textDecoration: selfCare[item.id] ? 'line-through' : 'none', fontFamily: 'Inter, sans-serif' }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Wellness Tools */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Wind size={16} color="#F97316" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>Wellness Exercises</span>
              </div>
              {WELLNESS_TOOLS.map(tool => {
                const Icon = tool.icon;
                return (
                  <div key={tool.id} style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid var(--border-subtle)', alignItems: 'flex-start' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={16} color="#F97316" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '3px' }}>{tool.title}</div>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 6px' }}>{tool.desc}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{tool.duration}</span>
                        <button
                          onClick={() => tool.id === 'breathing' && setShowBreathing(true)}
                          style={{ background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', color: '#F97316', fontSize: '12px', fontWeight: 600 }}
                          data-testid={`start-${tool.id}`}>
                          Start
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Crisis Resources */}
            <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '14px', padding: '20px' }} data-testid="crisis-resources">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <AlertCircle size={16} color="#EF4444" />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>Crisis Resources</span>
                <span style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', marginLeft: 'auto' }}>24/7</span>
              </div>
              <p style={{ fontSize: '13px', color: '#888888', margin: '0 0 14px', lineHeight: 1.6 }}>If you are in crisis or having thoughts of self-harm, please reach out immediately.</p>
              {CRISIS_RESOURCES.map(r => (
                <div key={r.name} style={{ padding: '12px 0', borderBottom: '1px solid rgba(239,68,68,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '2px' }}>{r.name}</div>
                      <div style={{ fontSize: '14px', color: '#EF4444', fontWeight: 700, marginBottom: '3px' }}>{r.detail}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{r.desc}</div>
                    </div>
                    {r.urgent && <span style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', flexShrink: 0 }}>URGENT</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
